(() => {
  'use strict';

  const ROOT = '[data-titan-cpb-root]';
  const PREVIEW = '.cpb-preview-container, .cpb-preview-wrapper, .cpb-preview';
  const PANELS = '.cpb-panels-container';
  const normalize = (value = '') => value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();
  const zoneTokens = (value = '') => normalize(value)
    .replace(/paso\s*\d+/g, ' ')
    .replace(/playeras/g, 'playera')
    .replace(/costados/g, 'costado')
    .replace(/\b(color|base|fondos?|texturas?|layer|test|de)\b/g, ' ')
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length > 2);
  const bestZoneIndex = (categories, sourceTitle) => {
    const source = normalize(sourceTitle);
    const sourceTokens = zoneTokens(sourceTitle);
    let bestIndex = -1;
    let bestScore = -1;
    categories.forEach((category, index) => {
      const candidate = normalize(category.title);
      const candidateTokens = zoneTokens(category.title);
      let score = sourceTokens.reduce((total, token) => total + (candidateTokens.includes(token) ? 2 : 0), 0);
      if (source.includes('selector') === candidate.includes('selector')) score += 3;
      if (source.includes('color') === candidate.includes('color')) score += 2;
      if (source.includes('arriba') === candidate.includes('arriba')) score += 2;
      if (source.includes('abajo') === candidate.includes('abajo')) score += 2;
      if (score > bestScore) { bestScore = score; bestIndex = index; }
    });
    return bestIndex;
  };
  const shortTitle = (title = '', index = 0) => {
    const clean = title.replace(/^paso\s*\d+\s*/i, '').trim();
    return clean || ['Base', 'Degradado', 'Texturas', 'Logo'][index] || `Fase ${index + 1}`;
  };

  function visible(node) {
    if (!node || !node.isConnected) return false;
    const style = getComputedStyle(node);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function interactiveByText(root, label) {
    const wanted = normalize(label);
    const matches = [...root.querySelectorAll('button, [role="tab"], [role="button"], .cpb-category-title, a, label')].filter((node) =>
      !node.closest('.titan-phase-guide') && normalize(node.textContent) === wanted
    );
    return matches.sort((a, b) => a.children.length - b.children.length)[0];
  }

  async function phaseData(root) {
    const url = root.dataset.cpbConfigUrl;
    if (url) {
      try {
        const response = await fetch(url, { credentials: 'omit' });
        if (response.ok) {
          const config = await response.json();
          const panels = config?.data?.panels;
          if (Array.isArray(panels) && panels.length) {
            const phases = panels.map((panel, index) => ({
              id: panel.id || String(index),
              title: panel.title || `Paso ${index + 1}`,
              count: Array.isArray(panel.categories) ? panel.categories.length : 0,
              categoryIds: (panel.categories || []).map((category) => category.id).filter(Boolean),
              categories: (panel.categories || []).map((category) => ({
                id: category.id,
                title: category.title || 'Zona',
                required: category.required === true,
                type: category.type,
                maskUrls: (category.options || []).map((option) => option.preview?.data?.front || (/^https?:/i.test(option.option?.data?.value || '') ? option.option.data.value : '')).filter(Boolean)
              }))
            }));
            const categories = new Map(phases.flatMap((phase) => phase.categories).map((category) => [category.id, category]));
            phases.categoryMeta = categories;
            phases.layerTargets = {};
            (config?.data?.customLayers || []).forEach((layer) => {
              const optionSource = layer.optionLayerImage?.enabled ? layer.optionLayerImage?.source?.category : null;
              const sourceId = (Array.isArray(optionSource) ? optionSource[0] : optionSource)
                || layer.fillPatternImageFromCategory?.source?.category
                || layer.colorize?.source?.category
                || layer.image?.source?.category
                || layer.text?.source?.category
                || layer.source?.category;
              // Reject orphaned layers from copied CPB products. Never guess a
              // different product zone from a similar layer title.
              if (!sourceId || !categories.has(sourceId)) return;
              const staticMask = layer.image?.defaultValue;
              phases.layerTargets[layer.id] = {
                categoryId: sourceId,
                maskUrls: staticMask ? [staticMask] : (categories.get(sourceId)?.maskUrls || []),
                staticMask: layer.type === 'img' ? staticMask : null
              };
            });
            return phases;
          }
        }
      } catch (_) { /* DOM discovery below is the safe fallback. */ }
    }

    const tabs = [...root.querySelectorAll('.cpb-panels-tabs [role="tab"], .cpb-panels-tabs button, [role="tab"]')];
    return tabs.slice(0, 6).map((tab, index) => ({ id: String(index), title: tab.textContent.trim() || `Paso ${index + 1}`, count: 0, categoryIds: [], categories: [] }));
  }

  function buildGuide(root, panelsContainer, phases) {
    if (!panelsContainer || panelsContainer.querySelector('.titan-phase-guide') || !phases.length) return null;
    const guide = document.createElement('section');
    guide.className = 'titan-phase-guide';
    guide.setAttribute('aria-label', 'Fases de personalización');
    guide.innerHTML = `
      <div class="titan-phase-guide__intro">
        <div><p>Construye tu armadura</p><h2>Diseña por fases</h2></div>
        <span class="titan-phase-guide__progress">Fase 01 de ${String(phases.length).padStart(2, '0')}</span>
      </div>
      <div class="titan-design-progress"><span data-design-progress-label>Tu recorrido</span><progress data-design-progress max="1" value="0" aria-label="Zonas revisadas"></progress></div>
      <div class="titan-phase-guide__track" aria-hidden="true"><span></span></div>
      <ol class="titan-phase-guide__steps"></ol>
      <div class="titan-phase-guide__zones" aria-live="polite"></div>`;
    const list = guide.querySelector('ol');
    phases.forEach((phase, index) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `titan-phase-guide__step${index === 0 ? ' is-active' : ''}`;
      button.dataset.phase = String(index);
      button.setAttribute('aria-current', index === 0 ? 'step' : 'false');
      button.innerHTML = `<span>${String(index + 1).padStart(2, '0')}</span><strong>${shortTitle(phase.title, index)}</strong><small>${phase.count ? `${phase.count} zonas` : 'Personalizar'}</small>`;
      item.append(button);
      list.append(item);
    });
    panelsContainer.prepend(guide);
    const productName = document.createElement('p');
    productName.className = 'titan-panel-product-name';
    productName.textContent = root.closest('.titan-cpb')?.querySelector('h1')?.textContent.trim() || '';
    panelsContainer.prepend(productName);
    renderZones(guide, phases, 0);
    return guide;
  }

  function renderZones(guide, phases, phaseIndex, zoneIndex = 0) {
    const phase = phases[phaseIndex];
    const zoneList = guide.querySelector('.titan-phase-guide__zones');
    if (!phase || !zoneList) return;
    const zones = phase.categories || [];
    const boundedIndex = Math.max(0, Math.min(zoneIndex, Math.max(0, zones.length - 1)));
    const category = zones[boundedIndex];
    const isFinalZone = boundedIndex === zones.length - 1 && availablePhase(phases, phaseIndex, 1) < 0;
    guide.dataset.activeZone = String(boundedIndex);
    const zoneNumber = phases.slice(0, phaseIndex).reduce((total, item) => total + item.categories.length, 0) + boundedIndex;
    const lightZone = zoneNumber % 2 === 1;
    guide.style.setProperty('--zone-heading-color', lightZone ? '#fff' : '#111');
    guide.style.setProperty('--zone-heading-text', lightZone ? '#111' : '#fff');
    zoneList.replaceChildren();
    zoneList.innerHTML = `
      <div class="titan-phase-guide__zone-head">
        <div><p>Editando ${String(phaseIndex + 1).padStart(2, '0')}.${String(boundedIndex + 1).padStart(2, '0')} · ${shortTitle(phase.title, phaseIndex)}</p><h3>${(category?.title || 'Selecciona una zona').replace(/^color\s+base\s*/i, '').replace(/^color\s*/i, '').replace(/^selector\s*/i, '').replace(/^de\s+/i, '').replace(/\bUDI\b/gi, 'Cuerpo')}</h3></div>
        <span>${category?.required ? 'Obligatorio' : 'Opcional'}</span>
      </div>
      <div class="titan-phase-guide__zone-track" aria-hidden="true"><span style="width:${((boundedIndex + 1) / Math.max(zones.length, 1)) * 100}%"></span></div>
      <div class="titan-phase-guide__detail" data-zone-detail aria-live="polite"></div>
      <p class="titan-zone-feedback" role="status" aria-live="polite">${category?.required ? 'Selecciona una opción para continuar.' : 'Puedes personalizar esta zona o continuar sin cambios.'}</p>
      <nav class="titan-phase-guide__zone-nav" aria-label="Navegación entre zonas">
        <button type="button" data-zone-nav="previous" ${phaseIndex === 0 && boundedIndex === 0 ? 'disabled' : ''}>Anterior</button>
        <button type="button" class="is-primary" data-zone-nav="next">${isFinalZone ? 'Revisar diseño' : boundedIndex === zones.length - 1 ? 'Siguiente fase' : 'Siguiente zona'}</button>
      </nav>`;
    updateDesignProgress(guide, phases);
  }

  function updateDesignProgress(guide, phases) {
    const reviewed = phases.reviewed || (phases.reviewed = new Set());
    const categories = phases.filter(phase => !phase.unavailable).flatMap(phase => phase.categories);
    const count = categories.filter(category => reviewed.has(category.id)).length;
    const label = guide.querySelector('[data-design-progress-label]');
    const bar = guide.querySelector('[data-design-progress]');
    if (label) label.textContent = `${count} de ${categories.length} zonas revisadas`;
    if (bar) { bar.max = Math.max(1, categories.length); bar.value = count; }
    phases.forEach((phase, index) => {
      guide.querySelector(`[data-phase="${index}"]`)?.classList.toggle('is-reviewed', phase.categories.length > 0 && phase.categories.every(category => reviewed.has(category.id)));
    });
  }

  function liveCategories(root) {
    return [...root.querySelectorAll('.cpb-panel-categories-container > .cpb-category')].filter((category) => {
      const style = getComputedStyle(category);
      return !category.hidden && style.display !== 'none' && style.visibility !== 'hidden';
    });
  }

  function orderedLiveCategories(root, guide) {
    const nodes = liveCategories(root);
    const mounted = guide?.querySelector('[data-titan-mounted-category="true"]');
    if (!mounted || !visible(mounted)) return nodes;
    const parent = mounted._titanOriginalParent;
    if (!parent?.isConnected) return [mounted, ...nodes];
    const siblings = nodes.filter((node) => node.parentNode === parent);
    const fallbackIndex = Number.isInteger(mounted._titanOriginalIndex) ? mounted._titanOriginalIndex : siblings.length;
    const nextIndex = mounted._titanOriginalNext && siblings.includes(mounted._titanOriginalNext)
      ? siblings.indexOf(mounted._titanOriginalNext)
      : Math.min(fallbackIndex, siblings.length);
    siblings.splice(nextIndex, 0, mounted);
    const otherNodes = nodes.filter((node) => node.parentNode !== parent);
    return [...siblings, ...otherNodes];
  }

  function syncLivePhase(guide, phases, phaseIndex, nodes) {
    const phase = phases[phaseIndex];
    if (!phase || !nodes.length) return;
    const liveCategories = nodes.map((node, index) => ({
      ...phases.categoryMeta?.get(node.dataset.categoryId),
      id: node.dataset.categoryId || node.getAttribute('data-category-id') || String(index),
      title: node.querySelector('.cpb-category-title')?.textContent.trim() || `Zona ${index + 1}`,
      node
    }));
    // CPB inserts dependent color controls after texture selection. A shorter
    // live list can still contain NEW ids; retaining the old list loses them.
    phase.categories = liveCategories.sort((a, b) => {
      const ai = phase.categoryIds.indexOf(a.id);
      const bi = phase.categoryIds.indexOf(b.id);
      return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
    });
    phase.hasLiveSync = true;
    phase.count = phase.categories.length;
    const step = guide.querySelector(`.titan-phase-guide__step[data-phase="${phaseIndex}"] small`);
    const countLabel = `${phase.count} ${phase.count === 1 ? 'zona' : 'zonas'}`;
    if (step && step.textContent !== countLabel) step.textContent = countLabel;
    phase.unavailable = false;
    guide.querySelector(`.titan-phase-guide__step[data-phase="${phaseIndex}"]`)?.closest('li')?.removeAttribute('hidden');
  }

  function markPhaseUnavailable(guide, phases, phaseIndex) {
    const phase = phases[phaseIndex];
    if (!phase) return;
    phase.categories = [];
    phase.count = 0;
    phase.unavailable = true;
    const step = guide.querySelector(`.titan-phase-guide__step[data-phase="${phaseIndex}"]`);
    if (step) step.closest('li').hidden = true;
  }

  function availablePhase(phases, from, direction) {
    for (let index = from + direction; index >= 0 && index < phases.length; index += direction) {
      if (!phases[index].unavailable && phases[index].categories.length) return index;
    }
    return -1;
  }

  function restoreMountedCategory(guide) {
    const mounted = guide?.querySelector('[data-titan-mounted-category="true"]');
    if (!mounted) return;
    const parent = mounted._titanOriginalParent;
    const next = mounted._titanOriginalNext;
    mounted.removeAttribute('data-titan-mounted-category');
    if (parent?.isConnected) parent.insertBefore(mounted, next?.parentNode === parent ? next : null);
  }

  function mountCategory(guide, categoryNode) {
    const slot = guide?.querySelector('[data-zone-detail]');
    if (!slot || !categoryNode) return false;
    const current = guide.querySelector('[data-titan-mounted-category="true"]');
    if (current && current !== categoryNode) restoreMountedCategory(guide);
    if (!categoryNode._titanOriginalParent) {
      categoryNode._titanOriginalParent = categoryNode.parentNode;
      categoryNode._titanOriginalNext = categoryNode.nextSibling;
      categoryNode._titanOriginalIndex = [...categoryNode.parentNode.children]
        .filter((node) => node.classList?.contains('cpb-category'))
        .indexOf(categoryNode);
    }
    categoryNode.dataset.titanMountedCategory = 'true';
    slot.append(categoryNode);
    categoryNode.hidden = false;
    categoryNode.removeAttribute('aria-hidden');
    return true;
  }

  function activatePhase(root, guide, phases, index, clickNative = true) {
    const phase = phases[index];
    if (!phase || !guide) return false;
    const phaseChanged = guide.dataset.activePhase !== String(index);
    if (clickNative) {
      const nativeTab = interactiveByText(root, phase.title);
      if (!nativeTab) return false;
      if (nativeTab.getAttribute('aria-selected') !== 'true' && !nativeTab.classList.contains('cpb-active')) nativeTab.click();
      const selected = [...root.querySelectorAll('.cpb-panels-tabs [aria-selected="true"], .cpb-panels-tabs .cpb-active, [role="tab"][aria-selected="true"]')]
        .find((node) => !node.closest('.titan-phase-guide'));
      if (!selected || normalize(selected.textContent) !== normalize(phase.title)) return false;
    }
    if (!clickNative && guide.dataset.activePhase === String(index)) return true;
    guide.dataset.activePhase = String(index);
    guide.querySelectorAll('.titan-phase-guide__step').forEach((button, buttonIndex) => {
      const active = buttonIndex === index;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-current', active ? 'step' : 'false');
    });
    guide.querySelector('.titan-phase-guide__progress').textContent = `Fase ${String(index + 1).padStart(2, '0')} de ${String(phases.length).padStart(2, '0')}`;
    guide.querySelector('.titan-phase-guide__track span').style.width = `${((index + 1) / phases.length) * 100}%`;
    if (phaseChanged) renderZones(guide, phases, index, 0);
    return true;
  }

  function openZone(root, guide, phases, phaseIndex, zoneIndex, targetTitle = '', targetId = '', scrollToControls = true) {
    if (!phases[phaseIndex]) return;
    restoreMountedCategory(guide);
    activatePhase(root, guide, phases, phaseIndex, true);
    const tryOpen = (attempt = 0) => {
      const selected = [...root.querySelectorAll('.cpb-panels-tabs [aria-selected="true"], .cpb-panels-tabs .cpb-active, [role="tab"][aria-selected="true"]')]
        .find((node) => !node.closest('.titan-phase-guide'));
      const selectedIndex = selected ? phases.findIndex((phase) => normalize(phase.title) === normalize(selected.textContent)) : -1;
      if (selectedIndex !== phaseIndex) {
        if (attempt < 12) setTimeout(() => tryOpen(attempt + 1), 120);
        return;
      }
      activatePhase(root, guide, phases, phaseIndex, false);
      const currentCategories = orderedLiveCategories(root, guide);
      if (currentCategories.length) syncLivePhase(guide, phases, phaseIndex, currentCategories);
      if (targetId) {
        const identifiedZone = phases[phaseIndex].categories.findIndex((category) => category.id === targetId);
        if (identifiedZone >= 0) zoneIndex = identifiedZone;
      } else if (targetTitle) {
        const matchedZone = bestZoneIndex(phases[phaseIndex].categories, targetTitle);
        if (matchedZone >= 0) zoneIndex = matchedZone;
      }
      const boundedZone = Math.max(0, Math.min(zoneIndex, Math.max(0, phases[phaseIndex].categories.length - 1)));
      const category = phases[phaseIndex].categories[boundedZone];
      if (!category) {
        if (attempt < 12) setTimeout(() => tryOpen(attempt + 1), 120);
        return;
      }
      renderZones(guide, phases, phaseIndex, boundedZone);
      const escapedId = CSS.escape(category.id || '');
      const identified = escapedId ? root.querySelector(`.cpb-category[data-category-id="${escapedId}"], [data-category-id="${escapedId}"]`) : null;
      const categoryNode = category.node?.isConnected ? category.node : identified?.closest('.cpb-category') || identified;
      const control = categoryNode?.matches('.cpb-category-title') ? categoryNode : categoryNode?.querySelector('.cpb-category-title') || interactiveByText(root, category.title);
      if (control) {
        const resolvedCategory = control.closest('.cpb-category') || categoryNode;
        if (control.getAttribute('aria-expanded') !== 'true' && !control.classList.contains('cpb-active')) control.click();
        if (resolvedCategory && mountCategory(guide, resolvedCategory)) {
          updateZoneFeedback(guide, phases);
          if (scrollToControls) {
            const heading = guide.querySelector('.titan-phase-guide__zone-head');
            heading.setAttribute('tabindex', '-1');
            heading.focus({ preventScroll: true });
            guide.querySelector('[data-zone-detail]').scrollTop = 0;
          }
          return;
        }
      } else if (attempt < 12) {
        setTimeout(() => tryOpen(attempt + 1), 100);
        return;
      }
      if (attempt < 12) setTimeout(() => tryOpen(attempt + 1), 100);
    };
    setTimeout(tryOpen, 40);
  }

  function numberCategories(root) {
    root.querySelectorAll('.cpb-panel-categories-container').forEach((container) => {
      [...container.querySelectorAll('.cpb-category-title')].forEach((title, index) => {
        if (!title.dataset.titanIndex) title.dataset.titanIndex = String(index + 1).padStart(2, '0');
      });
    });
  }

  function updateZoneFeedback(guide, phases, warn = false) {
    const phase = phases[Number(guide.dataset.activePhase || 0)];
    const category = phase?.categories[Number(guide.dataset.activeZone || 0)];
    const node = guide.querySelector('[data-titan-mounted-category]');
    const feedback = guide.querySelector('.titan-zone-feedback');
    if (!category || !node || !feedback) return true;
    const selected = !!node.querySelector('.cpb-option[aria-pressed="true"], .cpb-option.cpb-active, input:checked');
    const entered = [...node.querySelectorAll('textarea,input:not([type="file"]):not([type="hidden"]):not([type="radio"]):not([type="checkbox"])')].some((field) => field.value.trim() !== '' && (field.type !== 'number' || Number(field.value) > 0));
    // File upload processing remains owned and validated by CPB.
    const knownInput = category.type === 'single' || category.type === 'input' || category.type === 'inputLong';
    const missing = category.required && knownInput && !selected && !entered;
    warn = warn || feedback.classList.contains('is-error');
    const message = missing ? (warn ? 'Falta completar esta zona. Elige una opción o introduce el dato solicitado.' : 'Esta zona es obligatoria. Elige una opción para continuar.') : selected || entered ? 'Selección registrada. Puedes continuar o modificarla.' : category.required ? 'Completa el archivo solicitado. Se comprobará al agregar al carrito.' : 'Zona opcional. Puedes continuar sin cambios.';
    if (feedback.textContent !== message) feedback.textContent = message;
    feedback.classList.toggle('is-error', !!(missing && warn));
    return !missing;
  }

  function polishInteractiveControls(root) {
    const labels = {
      '.cpb-quantity-label': 'Cantidad',
      '.cpb-reset-button span': 'Restablecer',
      '.cpb-add-to-cart-button span': 'Agregar al carrito',
      '.cpb-prev-tab-button span': 'Fase anterior',
      '.cpb-next-tab-button:not(.cpb-prev-tab-button) span': 'Siguiente fase'
    };
    Object.entries(labels).forEach(([selector, label]) => {
      root.querySelectorAll(selector).forEach((node) => {
        if (node.textContent !== label) node.textContent = label;
      });
    });
    root.querySelectorAll('[aria-label]').forEach((node) => {
      const original = node.getAttribute('aria-label');
      const translated = { Quantity: 'Cantidad', 'Decrease quantity': 'Reducir cantidad', 'Increase quantity': 'Aumentar cantidad', 'Decrease value': 'Reducir valor', 'Increase value': 'Aumentar valor' }[original]
        || original.replace(/^Color option /, 'Color ');
      if (translated !== original) node.setAttribute('aria-label', translated);
    });
    root.querySelectorAll('.cpb-drop-zone').forEach((dropZone) => {
      dropZone.setAttribute('aria-label', 'Subir el escudo o logotipo del equipo');
      const title = dropZone.querySelector('.cpb-upload-title span');
      const dragging = dropZone.querySelector('.cpb-upload-title-hidden span');
      if (title && title.textContent !== 'Arrastra tu logo o haz clic para seleccionarlo') title.textContent = 'Arrastra tu logo o haz clic para seleccionarlo';
      if (dragging && dragging.textContent !== 'Suelta el archivo para cargarlo') dragging.textContent = 'Suelta el archivo para cargarlo';
    });
    root.querySelectorAll('.cpb-option-type-inputLong textarea').forEach((field) => {
      if (!field.placeholder) field.placeholder = 'Ej. TITAN';
      field.setAttribute('aria-label', 'Nombre que aparecerá en el uniforme');
      field.setAttribute('autocomplete', 'off');
      field.setAttribute('spellcheck', 'false');
    });
  }

  function installHotspots(root, guide, phases) {
    const preview = root.querySelector(PREVIEW);
    if (!preview || !guide || preview.dataset.titanHotspots === 'true') return;
    preview.dataset.titanHotspots = 'true';
    let suspended = false;
    let last = { x: -1, y: -1 };
    let maskCache = new WeakMap();
    const sourceMasks = new Map();
    Object.entries(phases.layerTargets || {}).forEach(([id, layer]) => {
      if (!layer.staticMask) return;
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => { sourceMasks.set(id, image); maskCache = new WeakMap(); };
      image.src = layer.staticMask;
    });

    const targetForCanvas = (canvas) => {
      const layerId = canvas.dataset.customLayer;
      const layerTarget = layerId ? phases.layerTargets?.[layerId] : null;
      if (layerId && !layerTarget) return null;
      if (layerTarget?.categoryId) {
        for (let phaseIndex = 0; phaseIndex < phases.length; phaseIndex += 1) {
          const zoneIndex = phases[phaseIndex].categories.findIndex((category) => category.id === layerTarget.categoryId);
          if (zoneIndex >= 0) return { phaseIndex, zoneIndex, title: phases[phaseIndex].categories[zoneIndex].title, categoryId: layerTarget.categoryId };
        }
      }
      const directId = canvas.dataset.category;
      if (directId && directId !== 'base') {
        for (let phaseIndex = 0; phaseIndex < phases.length; phaseIndex += 1) {
          const zoneIndex = phases[phaseIndex].categories.findIndex((category) => category.id === directId);
          if (zoneIndex >= 0) return { phaseIndex, zoneIndex, title: phases[phaseIndex].categories[zoneIndex].title, categoryId: directId };
        }
      }
      // Each product's config is the authority. A stale category must not
      // become an unrelated hotspot through fuzzy title matching.
      return null;
    };

    const pixel = (canvas, x, y) => canvas.getContext('2d', { willReadFrequently: true }).getImageData(x, y, 1, 1).data;
    const ownsPixel = (canvases, index, x, y) => {
      // Konva canvases are independent transparent layers, not cumulative frames.
      // Comparing adjacent colors incorrectly removes white fabric and text.
      return pixel(maskFor(canvases[index]), x, y)[3] > 3;
    };

    const maskFor = (canvas) => {
      if (maskCache.has(canvas)) return maskCache.get(canvas);
      const mask = document.createElement('canvas');
      mask.width = canvas.width; mask.height = canvas.height;
      const context = mask.getContext('2d');
      try {
        const source = sourceMasks.get(canvas.dataset.customLayer);
        if (source) {
          const ratio = Math.min(canvas.width / source.naturalWidth, canvas.height / source.naturalHeight);
          const width = source.naturalWidth * ratio, height = source.naturalHeight * ratio;
          context.drawImage(source, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
        } else context.drawImage(canvas, 0, 0);
        const currentData = context.getImageData(0, 0, canvas.width, canvas.height);
        const output = context.createImageData(canvas.width, canvas.height);
        for (let offset = 0; offset < currentData.data.length; offset += 4) {
          const alpha = currentData.data[offset + 3];
          if (alpha <= 16) continue;
          output.data[offset] = 223; output.data[offset + 1] = 24; output.data[offset + 2] = 36;
          output.data[offset + 3] = Math.round(alpha * .24);
        }
        context.putImageData(output, 0, 0);
      } catch (_) { context.clearRect(0, 0, mask.width, mask.height); }
      maskCache.set(canvas, mask);
      return mask;
    };

    const hitAt = (clientX, clientY) => {
      const activePhase = Number(guide.dataset.activePhase || 0);
      const priority = (canvas) => {
        const target = targetForCanvas(canvas);
        const isIdentity = /text|curved|logo|nombre|n[uú]mero/i.test(`${canvas.dataset.type} ${canvas.dataset.title}`) && !/textura/i.test(canvas.dataset.title || '');
        return isIdentity ? 2 : target?.phaseIndex === activePhase ? 1 : 0;
      };
      const canvases = [...preview.querySelectorAll('canvas:not(.titan-cpb-hotspot)')].sort((a, b) => priority(a) - priority(b));
      for (let index = canvases.length - 1; index >= 0; index -= 1) {
        const canvas = canvases[index];
        const target = targetForCanvas(canvas);
        if (!target) continue;
        const rect = canvas.getBoundingClientRect();
        if (!rect.width || !rect.height || clientX < rect.left || clientX >= rect.right || clientY < rect.top || clientY >= rect.bottom) continue;
        try {
          const x = Math.floor((clientX - rect.left) * canvas.width / rect.width);
          const y = Math.floor((clientY - rect.top) * canvas.height / rect.height);
          if (ownsPixel(canvases, index, x, y)) return { canvas, target };
        } catch (_) { continue; }
      }
      return null;
    };

    const clear = () => {
      preview.querySelector('.titan-cpb-hotspot')?.remove();
      preview.style.removeProperty('cursor');
    };
    const showHit = (hit) => {
      const overlay = document.createElement('canvas');
      overlay.width = hit.canvas.width; overlay.height = hit.canvas.height;
      overlay.style.cssText = hit.canvas.style.cssText;
      overlay.className = 'titan-cpb-hotspot';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.style.pointerEvents = 'none';
      overlay.style.zIndex = '100';
      overlay.getContext('2d').drawImage(maskFor(hit.canvas), 0, 0);
      hit.canvas.parentElement.append(overlay);
      preview.style.cursor = 'pointer';
    };
    const suspend = () => { suspended = true; clear(); };
    // Stop the vendor's rectangular hover before it reaches its Konva stage.
    // Native drag events remain available while a pointer button is held.
    ['mouseover', 'mouseenter', 'mousemove', 'pointerover'].forEach((type) => {
      preview.addEventListener(type, (event) => {
        if (!event.buttons && event.target.closest('.cpb-render-konva')) event.stopPropagation();
      }, true);
    });
    preview.addEventListener('pointermove', (event) => {
      if (event.buttons) { clear(); return; }
      if (event.target.closest('.cpb-render-konva')) event.stopPropagation();
      if (event.clientX !== last.x || event.clientY !== last.y) suspended = false;
      last = { x: event.clientX, y: event.clientY };
      clear();
      if (suspended) return;
      const hit = hitAt(event.clientX, event.clientY);
      if (!hit) return;
      showHit(hit);
    }, true);
    preview.addEventListener('click', (event) => {
      const hit = hitAt(event.clientX, event.clientY);
      const target = hit?.target;
      if (event.target.closest('.cpb-render-konva')) event.stopPropagation();
      clear();
      if (target) {
        showHit(hit);
        openZone(root, guide, phases, target.phaseIndex, target.zoneIndex, target.title, target.categoryId, false);
      }
    }, true);
    preview.addEventListener('mouseleave', clear);
    root.addEventListener('click', (event) => {
      if (event.target.closest('[role="option"], .cpb-option, .cpb-swatch, input[type="file"]')) maskCache = new WeakMap();
    }, true);
    root.addEventListener('input', () => { maskCache = new WeakMap(); clear(); }, true);
    ['scroll', 'wheel', 'touchmove', 'resize'].forEach((eventName) => window.addEventListener(eventName, suspend, { passive: true }));
  }

  async function readCartState() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    const cart = await response.json();
    return {
      signature: `${cart.item_count}:${cart.items.map((item) => `${item.key}:${item.quantity}`).join('|')}`,
      count: cart.item_count,
      items: cart.items.map((item) => ({ key: item.key, quantity: item.quantity }))
    };
  }

  function editCartContext() {
    const parameters = new URLSearchParams(window.location.search);
    const key = parameters.get('modifyProduct');
    const configId = parameters.get('configid');
    return key && configId ? { key, configId } : null;
  }

  async function replaceEditedCartLine(editContext, before, after) {
    const previousLine = before.items.find((item) => item.key === editContext.key);
    if (!previousLine) return;

    const currentLine = after.items.find((item) => item.key === editContext.key);
    const quantity = currentLine && currentLine.quantity > previousLine.quantity
      ? currentLine.quantity - previousLine.quantity
      : 0;
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: editContext.key, quantity })
    });
    if (!response.ok) throw new Error(`Cart replacement failed: ${response.status}`);
  }

  function markEditMode(root) {
    const editContext = editCartContext();
    if (!editContext) return null;
    root.dataset.titanEditCartItem = 'true';
    const updateLabel = () => {
      const button = root.querySelector('.cpb-add-to-cart-button, .cpb-add-to-cart');
      if (!button) return false;
      const textNodes = document.createTreeWalker(button, NodeFilter.SHOW_TEXT);
      let textNode = textNodes.nextNode();
      let updated = false;
      while (textNode) {
        if (/agregar al carrito|add to cart|update cart/i.test(textNode.nodeValue.trim())) {
          textNode.nodeValue = textNode.nodeValue.replace(/agregar al carrito|add to cart|update cart/i, 'ACTUALIZAR PERSONALIZACIÓN');
          updated = true;
        }
        textNode = textNodes.nextNode();
      }
      button.setAttribute('aria-label', 'Actualizar personalización en el carrito');
      return updated || /actualizar personalización/i.test(button.textContent);
    };
    const observer = new MutationObserver(updateLabel);
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    updateLabel();
    window.setTimeout(() => observer.disconnect(), 15000);
    return editContext;
  }

  async function openUpdatedCartDrawer() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}?sections=cart-drawer,cart-icon-bubble`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`Cart sections request failed: ${response.status}`);
    const sections = await response.json();
    const drawer = document.querySelector('cart-drawer');
    if (!drawer?.renderContents) return false;
    drawer.renderContents({ id: 'cpb', sections });
    return true;
  }

  function installCartDrawerBridge(root) {
    if (root.dataset.titanCartBridge === 'true') return;
    root.dataset.titanCartBridge = 'true';
    const editContext = markEditMode(root);
    const editBaseline = editContext ? readCartState().catch(() => null) : null;
    root.addEventListener('click', async (event) => {
      const addButton = event.target.closest('.cpb-add-to-cart-button, .cpb-add-to-cart');
      if (!addButton || addButton.disabled || addButton.getAttribute('aria-disabled') === 'true') return;

      let before;
      try {
        before = editContext ? await editBaseline : await readCartState();
        if (!before) before = await readCartState();
      } catch (_error) { return; }
      let attempt = 0;
      const watchCart = window.setInterval(async () => {
        attempt += 1;
        try {
          const after = await readCartState();
          if (after.signature !== before.signature) {
            window.clearInterval(watchCart);
            if (editContext) {
              try {
                await replaceEditedCartLine(editContext, before, after);
              } catch (error) {
                console.error('[Titan CPB] The edited cart line could not be replaced.', error);
              }
            }
            const opened = await openUpdatedCartDrawer();
            if (!opened) window.location.assign(window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`);
          } else if (attempt >= 60) {
            window.clearInterval(watchCart);
          }
        } catch (_error) {
          if (attempt >= 60) window.clearInterval(watchCart);
        }
      }, 750);
    }, true);
  }

  function createLoaderProgress(loading) {
    if (!loading) return { set() {}, complete(callback) { callback?.(); } };
    const visual = loading.querySelector('[data-titan-cpb-progress]');
    const label = loading.querySelector('[data-titan-cpb-progress-value]');
    let current = 0;
    let target = 8;
    let completing = false;
    let finished = false;
    const update = (value) => {
      const rounded = Math.max(0, Math.min(100, Math.round(value)));
      visual?.style.setProperty('--titan-loader-progress', String(rounded));
      if (label) label.textContent = `${rounded}%`;
      loading.setAttribute('aria-valuenow', String(rounded));
    };
    update(0);
    const interval = window.setInterval(() => {
      if (finished || current >= target) return;
      const distance = target - current;
      current = Math.min(target, current + Math.max(.45, distance * .09));
      update(current);
    }, 45);
    const milestones = [
      [250, 18],
      [850, 34],
      [1700, 48],
      [3000, 61],
      [5000, 70]
    ].map(([delay, value]) => window.setTimeout(() => {
      if (!completing) target = Math.max(target, value);
    }, delay));
    return {
      set(value) {
        if (!completing) target = Math.max(target, Math.min(98, Number(value) || 0));
      },
      complete(callback) {
        if (completing) return;
        completing = true;
        target = 100;
        const finish = window.setInterval(() => {
          if (current < 99.5) return;
          window.clearInterval(finish);
          window.clearInterval(interval);
          milestones.forEach(window.clearTimeout);
          current = 100;
          finished = true;
          update(100);
          loading.classList.add('is-complete');
          window.setTimeout(() => callback?.(), 360);
        }, 45);
      }
    };
  }

  async function enhance(studio) {
    const root = studio.querySelector(ROOT);
    const loading = studio.querySelector('[data-titan-cpb-loading]');
    if (!root || root.dataset.titanEnhanced === 'true') return;
    const loaderProgress = createLoaderProgress(loading);
    root.dataset.titanEnhanced = 'true';
    installCartDrawerBridge(root);
    const hasVisibleCpbLoader = () => [...root.querySelectorAll('.cpb-loader, .cpb-aside-suspense')].some((node) => {
      const style = window.getComputedStyle(node);
      const rect = node.getBoundingClientRect();
      return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity || 1) > 0 && rect.width > 0 && rect.height > 0;
    });
    const fitViewport = () => {
      const top = Math.max(0, studio.getBoundingClientRect().top + window.scrollY);
      const previewBar = [...document.querySelectorAll('iframe')].some((frame) => /preview-bar/.test(frame.src) && frame.getBoundingClientRect().height > 30 && getComputedStyle(frame).visibility !== 'hidden');
      const height = Math.max(360, (window.visualViewport?.height || window.innerHeight) - top - (previewBar ? 62 : 8));
      studio.style.setProperty('--studio-height', `${Math.round(height)}px`);
      const preview = root.querySelector('.cpb-preview-container');
      if (preview) {
        const rect = preview.getBoundingClientRect();
        const edge = Math.max(80, Math.min(rect.width - 16, rect.height - 16));
        const wrapper = preview.querySelector('.cpb-render-wrapper');
        const canvas = preview.querySelector('canvas');
        if (wrapper && canvas) {
          const nativeWidth = parseFloat(canvas.style.width) || canvas.width;
          const base = preview.querySelector('canvas[data-category="base"]');
          if (base && !studio._baseBounds) {
            try {
              const pixels = base.getContext('2d').getImageData(0, 0, base.width, base.height).data;
              let left = base.width, top = base.height, right = 0, bottom = 0;
              for (let y = 0; y < base.height; y += 2) for (let x = 0; x < base.width; x += 2) {
                if (pixels[(y * base.width + x) * 4 + 3] <= 8) continue;
                left = Math.min(left, x); right = Math.max(right, x + 2);
                top = Math.min(top, y); bottom = Math.max(bottom, y + 2);
              }
              if (right > left && bottom > top) {
                studio._baseBounds = [left / base.width, top / base.height, right / base.width, bottom / base.height];
                studio.dataset.baseBounds = JSON.stringify(studio._baseBounds);
              }
            } catch (_) { /* Cross-origin canvas: retain the safe full-frame fit. */ }
          }
          const bounds = studio._baseBounds;
          const scale = bounds ? Math.min((rect.width - 32) / (nativeWidth * (bounds[2] - bounds[0])), (rect.height - 32) / (nativeWidth * (bounds[3] - bounds[1]))) : edge / nativeWidth;
          wrapper.style.setProperty('--studio-scale', String(scale));
          wrapper.style.setProperty('--studio-offset-x', bounds ? `${(0.5 - (bounds[0] + bounds[2]) / 2) * 100}%` : '0%');
          wrapper.style.setProperty('--studio-offset-y', bounds ? `${(0.5 - (bounds[1] + bounds[3]) / 2) * 100}%` : '0%');
        }
      }
    };
    window.addEventListener('resize', fitViewport, { passive: true });
    window.visualViewport?.addEventListener('resize', fitViewport, { passive: true });
    new ResizeObserver(fitViewport).observe(studio);
    fitViewport();
    [500, 1200, 2500, 5000].forEach(delay => setTimeout(fitViewport, delay));
    const phases = await phaseData(root);
    loaderProgress.set(74);
    let guide;
    let observer;
    let timer;
    root.addEventListener('input', () => { if (guide) updateZoneFeedback(guide, phases); });
    // Keep CPB's authoritative validation and make its error actionable.
    const validationObserver = new MutationObserver(() => {
      const dialog = document.querySelector('.swal2-popup');
      const title = dialog?.querySelector('.swal2-title');
      if (!dialog || title?.textContent.trim() !== 'Product Incomplete') return;
      title.textContent = 'Faltan opciones por completar';
      const content = dialog.querySelector('.swal2-html-container, .swal2-content');
      if (content) {
        [...content.childNodes].filter((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()).forEach((node) => {
          node.textContent = 'Completa las siguientes opciones. Puedes pulsar una para volver a esa zona:';
        });
        content.querySelectorAll('li').forEach((item) => {
          const label = item.textContent.trim();
          const phaseIndex = phases.findIndex((phase) => label.includes(phase.title));
          if (phaseIndex < 0) return;
          const category = [...(phases.categoryMeta?.values() || [])].find((entry) => label.endsWith(entry.title) && phases[phaseIndex].categoryIds.includes(entry.id));
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'titan-validation-link';
          button.textContent = category?.title || shortTitle(phases[phaseIndex].title, phaseIndex);
          button.addEventListener('click', () => {
            dialog.querySelector('.swal2-confirm')?.click();
            if (guide) openZone(root, guide, phases, phaseIndex, 0, '', category?.id || '');
          });
          item.replaceChildren(button);
        });
      }
      const confirm = dialog.querySelector('.swal2-confirm');
      if (confirm) confirm.textContent = 'Volver al diseño';
    });
    validationObserver.observe(document.body, { childList: true, subtree: true });

    root.addEventListener('click', (event) => {
      const mounted = event.target.closest('[data-titan-mounted-category="true"]');
      const editableControl = event.target.closest('textarea, input[type="text"], input:not([type]), input[type="file"], .cpb-drop-zone, .cpb-option-type-inputLong, .cpb-option-dropzone');
      if (editableControl) return;
      const option = event.target.closest('button, [role="option"], .cpb-option, .cpb-swatch, input[type="radio"], input[type="checkbox"], label');
      if (!guide || !mounted || !option || option.closest('.cpb-category') !== mounted) return;
      const phaseIndex = Number(guide.dataset.activePhase || 0);
      const currentId = mounted.dataset.categoryId || mounted.getAttribute('data-category-id') || '';

      // CPB conditionally mounts dependent categories (for example the color
      // that follows a texture). Restore its React node before the native click
      // bubbles so CPB can reconcile and insert that dependent category. The
      // selection never advances the Titan guide: only its navigation controls
      // may change the current zone or phase.
      restoreMountedCategory(guide);
      setTimeout(() => {
        const nodes = orderedLiveCategories(root, guide);
        if (!nodes.length) return;
        syncLivePhase(guide, phases, phaseIndex, nodes);
        const currentIndex = phases[phaseIndex].categories.findIndex((category) => category.id === currentId);
        openZone(root, guide, phases, phaseIndex, Math.max(0, currentIndex), '', currentId);
      }, 180);
    }, true);

    const sync = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const panelsContainer = root.querySelector(PANELS);
        const preview = root.querySelector(PREVIEW);
        if (panelsContainer && (!guide || !guide.isConnected)) {
          guide = buildGuide(root, panelsContainer, phases);
          if (guide) root.classList.add('has-titan-phase-guide');
          guide?.addEventListener('click', (event) => {
            const directionButton = event.target.closest('[data-zone-nav]');
            if (directionButton) {
              let phaseIndex = Number(guide.dataset.activePhase || 0);
              let zoneIndex = Number(guide.dataset.activeZone || 0);
              if (directionButton.dataset.zoneNav === 'next') {
                if (!updateZoneFeedback(guide, phases, true)) {
                  guide.querySelector('[data-zone-detail]').scrollTop = 0;
                  return;
                }
                (phases.reviewed || (phases.reviewed = new Set())).add(phases[phaseIndex].categories[zoneIndex].id);
                updateDesignProgress(guide, phases);
                if (zoneIndex < phases[phaseIndex].categories.length - 1) zoneIndex += 1;
                else {
                  const nextPhase = availablePhase(phases, phaseIndex, 1);
                  if (nextPhase >= 0) { phaseIndex = nextPhase; zoneIndex = 0; }
                  else { root.querySelector('.cpb-add-to-cart-button, .cpb-add-to-cart')?.focus({ preventScroll: true }); return; }
                }
              } else if (zoneIndex > 0) zoneIndex -= 1;
              else {
                const previousPhase = availablePhase(phases, phaseIndex, -1);
                if (previousPhase >= 0) { phaseIndex = previousPhase; zoneIndex = Math.max(0, phases[phaseIndex].categories.length - 1); }
              }
              openZone(root, guide, phases, phaseIndex, zoneIndex);
              return;
            }
            const button = event.target.closest('.titan-phase-guide__step[data-phase]');
            if (button) openZone(root, guide, phases, Number(button.dataset.phase), 0);
          });
          if (guide && phases[0]?.categories?.length) openZone(root, guide, phases, 0, 0, '', '', false);
        }
        if (guide) {
          const selected = [...root.querySelectorAll('.cpb-panels-tabs [aria-selected="true"], .cpb-panels-tabs .cpb-active, [role="tab"][aria-selected="true"]')].find((node) => !node.closest('.titan-phase-guide'));
          const selectedIndex = selected ? phases.findIndex((phase) => normalize(phase.title) === normalize(selected.textContent)) : -1;
          const activeIndex = Number(guide.dataset.activePhase || 0);
          // CPB can transiently select a native phase after an option click.
          // Do not mirror that internal state into the visible guide or attach
          // its categories to the wrong phase; explicit Titan controls own
          // navigation once the guide is mounted.
          if (selectedIndex < 0 || selectedIndex === activeIndex) {
            const currentCategories = orderedLiveCategories(root, guide);
            if (currentCategories.length) syncLivePhase(guide, phases, activeIndex, currentCategories);
          }
          updateZoneFeedback(guide, phases);
        }
        numberCategories(root);
        polishInteractiveControls(root);
        fitViewport();
        installHotspots(root, guide, phases);
        const usableControls = root.querySelectorAll('.cpb-category-title, .cpb-option, [role="option"], input').length;
        const usablePreview = preview && (preview.querySelector('canvas, img, svg') || preview.getBoundingClientRect().height > 200);
        if (panelsContainer) loaderProgress.set(82);
        if (usableControls) loaderProgress.set(90);
        if (usablePreview) loaderProgress.set(96);
        if (panelsContainer && usableControls && usablePreview && !hasVisibleCpbLoader() && !root.classList.contains('is-titan-ready')) {
          root.classList.add('is-titan-ready');
          loaderProgress.complete(() => loading?.setAttribute('hidden', ''));
        }
      }, 60);
    };
    observer = new MutationObserver(sync);
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-selected', 'aria-expanded'] });
    sync();
    setTimeout(() => {
      if (!root.classList.contains('is-titan-ready') && loading) {
        loading.classList.add('is-delayed');
        loading.querySelector('strong').textContent = 'El estudio sigue cargando';
        loading.querySelector('small').textContent = 'Ya puedes continuar cuando aparezcan las opciones.';
      }
    }, 15000);
  }

  const start = () => document.querySelectorAll('[data-titan-cpb-studio]').forEach(enhance);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start, { once: true }) : start();
  document.addEventListener('shopify:section:load', start);
})();
